import React, { useContext, useEffect, useState } from 'react'
import { Autocomplete, Divider, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import contactStyle from '../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import popupStyle from "../../../../assets/styles/componentStyles/globalPopupStyles.module.scss"
import globalStyles from '../../../../assets/styles/global.module.scss'
import chatStyles from "../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss"
import style from "../../../../assets/styles/componentStyles/customApiStyles/customApiStyle.module.scss"
import dashboardStyles from '../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss'
import Required from '../../../utility/required'
import GlobalButton from '../../../utility/globalButton/button'
import GlobalTooltip from '../../../utility/tooltip/globalTooltip'
import GlobalDeletePopup from '../../../utility/globalDeletePopup/globalDeletePopup'
import { ChatbotContext, ChatbotContextPayload } from '../../../../context/chatbotContext'
import { UserContext, UserContextPayload } from '../../../../context/userContext'
import { assignedChatbotForKB, AssignKnowledgeBaseChatbotRequest, chatbotListInterface, globalMenuItemData, knowledgeBaseAssignments, knowledgeBaseFiles } from '../../../../models/axiosModel'
import GlobalMenu from '../../../globalMenu'
import agent from '../../../../services/api'
import { useGenericStore } from '../../../../store/genericStore'
import { useShallow } from 'zustand/react/shallow'
import { primaryColor } from '../../../../utils/colors'
import useGenericHook from '../../../../hooks/useGenericHook'
import deleteDoc from "../../../../assets/deleteDoc.svg"
import { filterCustomChatbots } from '../../../../utils/constants'

interface AssignChatbotPopupProps {
    setFlagAssignChatbotPopup: React.Dispatch<React.SetStateAction<boolean>>,
    selectedFile: knowledgeBaseFiles | null,
    getKnowledgeBaseFilesApiCall: () => Promise<void>,
}

const AssignChatbotPopup: React.FC<AssignChatbotPopupProps> = (props) => {

    const {
        setFlagAssignChatbotPopup,
        selectedFile,
        getKnowledgeBaseFilesApiCall,
    } = props;

    const { chatbotList, GetChatbotListApi } = useContext(ChatbotContext) as ChatbotContextPayload;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const { isScreenBelow600 } = useGenericHook();

    const [selectedChatbot, setSelectedChatbot] = useState<chatbotListInterface | null>(null);
    const [chatbotToRemove, setChatbotToRemove] = useState<assignedChatbotForKB | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);

    const assignedChatbots = selectedFile?.assignments ?? [];

    const getChatbotListApiCall = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await GetChatbotListApi();
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    useEffect(() => {
        if (!chatbotList) {
            getChatbotListApiCall();
        }
    }, []);

    const getChatbotOptions = (): chatbotListInterface[] => {
        if (!chatbotList) return [];

        const assignedChatbotIds = assignedChatbots.map(
            (assignment) => String(assignment?.chatbot?.chatbot_id)
        );

        return filterCustomChatbots(chatbotList)
            .filter((chatbot: chatbotListInterface) => !assignedChatbotIds.includes(String(chatbot.chatbot_id)));
    }

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, chatbot: assignedChatbotForKB) => {
        event.stopPropagation();
        setChatbotToRemove(chatbot);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleClose = () => {
        setSelectedChatbot(null);
        setChatbotToRemove(null);
        setFlagAssignChatbotPopup(false);
    }

    const handleAssignChatbot = async () => {
        if (!selectedChatbot || !selectedFile) return;

        const payload: AssignKnowledgeBaseChatbotRequest = {
            flag_remove_chatbot: false,
            knowledge_base_file_id: selectedFile.knowledge_base_file_id,
            chatbot_id: Number(selectedChatbot.chatbot_id),
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await agent.updateAIChatbotKnowledgeBase.put(payload);
        if (!(res.status)) {
            handleError(res);
        } else {
            showSuccess("Chatbot assigned successfully")
            handleClose();
            await getKnowledgeBaseFilesApiCall();
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    const handleRemoveChatbotAssignment = async () => {
        if (!chatbotToRemove || !selectedFile) return;

        const payload: AssignKnowledgeBaseChatbotRequest = {
            flag_remove_chatbot: true,
            knowledge_base_file_id: selectedFile.knowledge_base_file_id,
            chatbot_id: Number(chatbotToRemove.chatbot_id),
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await agent.updateAIChatbotKnowledgeBase.put(payload);
        if (!(res.status)) {
            handleError(res);
        } else {
            showSuccess("Chatbot removed successfully")
            handleClose();
            await getKnowledgeBaseFilesApiCall();
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    const menuItem: globalMenuItemData[] = [
        {
            type: "item",
            label: "Remove",
            icon: <img alt="" src={deleteDoc} />,
            onClick: (chatbot: assignedChatbotForKB) => {
                setChatbotToRemove(chatbot);
                setShowDeleteConfirmation(true);
            }
        }
    ]

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container direction={"column"} justifyContent={"center"}>
                        <Grid item textAlign={"center"}>
                            <Typography className={popupStyle.popupTitleTypography}>
                                Assign Chatbot
                            </Typography>
                        </Grid>

                        <Divider className={popupStyle.popupTitleDivider} />

                        <Grid item>
                            <Grid container className={popupStyle.popupBodyGridContainer} gap={1}>
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"} className={globalStyles.relativePosition}>
                                        <Grid item xs={12}>
                                            <Typography className={popupStyle.popupFieldTypography}>
                                                Chatbot <Required />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container gap={1.5} flexWrap={"nowrap"}>
                                                <Grid item xs={isScreenBelow600 ? 10 : 10.9}>
                                                    <Autocomplete
                                                        className={`${contactStyle.contactFilterDropDowns} ${contactStyle.contactFilterDropdownsInPopup}`}
                                                        options={getChatbotOptions()}
                                                        getOptionLabel={(option: chatbotListInterface) => option?.chatbot_name || ""}
                                                        value={getChatbotOptions()?.find((chatbot) => chatbot?.chatbot_id === selectedChatbot?.chatbot_id) ?? null}
                                                        isOptionEqualToValue={(option, value) => option?.chatbot_id === value?.chatbot_id}
                                                        onChange={(_, value: chatbotListInterface | null) => setSelectedChatbot(value)}
                                                        ListboxProps={{
                                                            className: chatStyles.autocompleteListbox,
                                                        }}
                                                        renderInput={(params) => (<TextField {...params} placeholder="Select Chatbot" />)}
                                                        renderOption={(props, option) => {
                                                            return <li {...props} key={`${option?.chatbot_id}-${option?.chatbot_name}`}>
                                                                {option?.chatbot_name ?? ''}
                                                            </li>
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={isScreenBelow600 ? 1 : 0.5}>
                                                    <GlobalTooltip
                                                        title="Fetch latest chatbots"
                                                        placement='top'
                                                        arrow
                                                    >
                                                        <IconButton
                                                            className={contactStyle.headerButtonStyle}
                                                            onClick={() => getChatbotListApiCall()}
                                                        >
                                                            <RefreshIcon sx={{ color: primaryColor }} />
                                                        </IconButton>
                                                    </GlobalTooltip>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} padding={"20px 0"}>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Cancel"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='borderedButtonStyle'
                                        onClick={handleClose}
                                    >
                                    </GlobalButton>
                                </Grid>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Assign"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='primaryButtonStyle'
                                        disabled={selectedChatbot == null}
                                        onClick={handleAssignChatbot}
                                    >
                                    </GlobalButton>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container className={popupStyle.popupBodyGridContainer} gap={1}>
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.assignedChatbotText}>Assigned Chatbot</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TableContainer className={`${style.customAPITableContainer} ${style.assignedChatbotTableContainer}`}>
                                                <Table stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Chatbot Name</TableCell>
                                                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.tableHeaderMorevertCell}`}></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {assignedChatbots.length > 0 ?
                                                            assignedChatbots.map((assignment: knowledgeBaseAssignments) => {
                                                                const chatbot = assignment?.chatbot;
                                                                return (
                                                                    <TableRow key={assignment.knowledge_base_file_assignment_id} className={`${dashboardStyles.cellRow}`}>
                                                                        <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{chatbot?.chatbot_name || ""}</TableCell>
                                                                        <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon}`} onClick={(e) => e.stopPropagation()}>
                                                                            <MoreVertIcon
                                                                                className={`${globalStyles.clickable}`}
                                                                                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => handleMenuOpen(e, chatbot)}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })
                                                            :
                                                            <TableRow className={dashboardStyles.cellRow}>
                                                                <TableCell colSpan={12} className={`${globalStyles.textCenter}`}>
                                                                    No Data Available
                                                                </TableCell>
                                                            </TableRow>
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <GlobalMenu
                anchorEl={anchorEl}
                handleMenuClose={handleMenuClose}
                menuItemData={menuItem}
                open={Boolean(anchorEl)}
                additionalField={chatbotToRemove}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            />

            {showDeleteConfirmation &&
                <GlobalDeletePopup
                    deleteDescription={`Are you sure want to remove ${chatbotToRemove?.chatbot_name} chatbot from ${selectedFile?.file_name}`}
                    title="Remove Confirmation"
                    primaryButtonText="Remove"
                    open={showDeleteConfirmation}
                    onClose={() => setShowDeleteConfirmation(false)}
                    onDelete={() => {
                        if (chatbotToRemove) {
                            handleRemoveChatbotAssignment()
                        }
                        setShowDeleteConfirmation(false)
                    }}
                />
            }
        </>
    )
}

export default AssignChatbotPopup
